import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { hapticSuccess } from '../../../_utils/haptics';
import { isLiveDone, onboardingIdentity, useOnboarding } from '../ImportJobContext';
import { FIRST_TASK_EVENT } from '../firstTaskRegistry';
import { SETUP_V2_ENABLED } from '../flags';
import { useReception } from '../../../_utils/receptionMode';
import { PRACTICE_ARM_MS, PRACTICE_TASKS, chaptersFor, practiceDiaryStep, totalMinutes } from './setupPlan';

/**
 * "Set up with Bailey": where the owner (or stylist) is, and everything they
 * have answered so far.
 *
 * Dropping out at any moment loses nothing. Every change is written to this
 * browser and to the server's onboarding state (checklist.setup), so the
 * plan resumes on the exact chapter and question, on any device and after
 * signing out. When the two copies differ they're merged, never swapped:
 * see mergeSetup.
 *
 * The server keeps one plan per person: the account holder's is the salon's
 * plan, and everyone else (an invited self-employed stylist, employed staff)
 * reads and writes their own slot through the same endpoint, so a stylist
 * never overwrites the owner's. The practice record (checklist.practice) and
 * the practice tasks (checklist.tasks) live there too, so go-live can always
 * clean the practice client and booking up.
 */

export const SETUP_OPEN_EVENT = 'beu:setup-open';
// A request can arrive before the profile has loaded (the sign-up redirect),
// when nothing is listening yet. It waits here until SetupHost is ready.
let pendingOpen = null;
export const openSetup = (detail = {}) => {
  pendingOpen = detail;
  window.dispatchEvent(new CustomEvent(SETUP_OPEN_EVENT, { detail }));
};
export const takePendingOpen = () => { const p = pendingOpen; pendingOpen = null; return p; };

/**
 * A chapter's status: done when the person finished it here OR its data is
 * already in the salon (fact true). Otherwise what they chose (skipped), or
 * nothing yet.
 */
export function statusFrom(own, fact) {
  if (own === 'done' || fact === true) return 'done';
  return own || null;
}

const SetupContext = createContext(null);
// The add-location page renders the same layer for its own track (role
// 'location'), with a value built from the location intent: see AddLocationFlow.
export { SetupContext };
const now = () => new Date().toISOString();
const blank = () => ({ v: 1, status: 'new', current: null, chapters: {}, steps: {}, answers: {}, confirm: {}, stylistsReady: {}, updatedAt: null });

const storageKey = (userId, salonId) => `beu:setup:v1:${userId || 'anon'}:${salonId || 'none'}`;
const readLocal = (key) => { try { return JSON.parse(localStorage.getItem(key)) || null; } catch (e) { return null; } };
const writeLocal = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage full or blocked */ } };

/* ---------- two copies, one plan ----------
 * The browser's copy and the server's can each hold work the other hasn't
 * seen (the laptop yesterday, the phone today, two tabs). Rather than one
 * replacing the other they're merged: a finished chapter stays finished,
 * answers from both are kept (the newer copy's value wins where both
 * answered), and the newer copy says where they are now. A staging reset
 * (resetAt) is the one thing that wipes an older copy.
 */
const MARK_RANK = { skipped: 1, done: 2 };
const STATUS_RANK = { new: 0, parked: 1, active: 2, done: 3 };
const stamp = (x) => String((x && x.updatedAt) || '');
const mergeMaps = (older, newer) => ({ ...(older || {}), ...(newer || {}) });
export function mergeSetup(a, b) {
  if (!a || !a.updatedAt) return b || a;
  if (!b || !b.updatedAt) return a;
  const [older, newer] = stamp(a) <= stamp(b) ? [a, b] : [b, a];
  if (newer.resetAt && String(newer.resetAt) >= stamp(older)) return newer;
  const chapters = { ...(older.chapters || {}) };
  Object.keys(newer.chapters || {}).forEach((id) => {
    const st = newer.chapters[id];
    if ((MARK_RANK[st] || 0) >= (MARK_RANK[chapters[id]] || 0)) chapters[id] = st;
  });
  const answers = { ...(older.answers || {}) };
  Object.keys(newer.answers || {}).forEach((k) => { answers[k] = mergeMaps(answers[k], newer.answers[k]); });
  return {
    ...older,
    ...newer,
    status: (STATUS_RANK[older.status] || 0) > (STATUS_RANK[newer.status] || 0) ? older.status : newer.status,
    chapters,
    answers,
    steps: mergeMaps(older.steps, newer.steps),
    confirm: mergeMaps(older.confirm, newer.confirm),
    stylistsReady: mergeMaps(older.stylistsReady, newer.stylistsReady),
    startedAt: [older.startedAt, newer.startedAt].filter(Boolean).sort()[0] || null,
  };
}
// Key order doesn't matter when comparing two copies.
const stable = (v) => {
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  if (v && typeof v === 'object') return `{${Object.keys(v).sort().filter((k) => v[k] !== undefined).map((k) => `${JSON.stringify(k)}:${stable(v[k])}`).join(',')}}`;
  return JSON.stringify(v === undefined ? null : v);
};
const same = (a, b) => stable(a || null) === stable(b || null);

/**
 * owner    the account holder (salon owner, or a self-employed business on
 *          its own plan)
 * stylist  a self-employed stylist invited into someone else's salon
 * staff    employed staff
 */
// An id, whether the payload has it bare or as a populated document.
const idOf = (v) => {
  if (!v) return '';
  if (typeof v === 'object') return String(v._id || v.id || v.userId || '');
  return String(v);
};

export function setupRoleFor(user) {
  const h = (user && user.userInfo && user.userInfo.hairdresser) || null;
  if (!h) return null;
  if (Number(h.isSalonOwner) === 1) return 'owner';
  if (Number(h.employmentTypeId) === 2) {
    // A self-employed plan sign-up is the account holder of their own
    // business, even though its record says "not an owner" (the same flags
    // as a stylist invited into someone else's salon). The salon record
    // names its owner by user id, which tells the two apart.
    const salon = (h.salon && typeof h.salon === 'object') ? h.salon : {};
    const info = user.userInfo || {};
    const me = String(info.userId || info._id || localStorage.getItem('userId') || '');
    const owner = idOf(salon.salonOwner) || idOf(salon.userId) || idOf(salon.createdBy) || idOf(salon.ownerId);
    if (owner && me && owner === me) return 'owner';
    return 'stylist';
  }
  // Employed staff, and account managers (isSalonOwner 2), who help run the
  // salon but are not the account holder.
  return 'staff';
}

export function SetupProvider({ children }) {
  const user = useSelector((s) => s.user);
  // Setup never appears on a device locked in reception mode, whatever the
  // front desk's switches say: `enabled` false hides the Settings card, the
  // pill, the pet's way back in and the guided setup itself.
  const { setupOff } = useReception();
  const ob = useOnboarding() || {};
  const { onboarding, patchOnboarding } = ob;
  const who = onboardingIdentity(user);
  // The server's copy counts only once it has really been read, and only if
  // it's this person's: before that, a stale browser copy could overwrite
  // newer progress made on another device, or the last person's state could
  // be merged into the next one's after a sign-out.
  const serverMine = !!ob.onboardingLoaded && !!onboarding && !!who.key && ob.onboardingFor === who.key && ob.identity === who.key;
  const feRole = SETUP_V2_ENABLED ? setupRoleFor(user) : null;
  // The server knows who holds the account (it names the salon's owner);
  // the profile doesn't always carry that, so a self-employed business on
  // its own plan can read as 'stylist' here. The server's word wins.
  const role = feRole && feRole !== 'owner' && serverMine && onboarding.role === 'owner' ? 'owner' : feRole;
  const loaded = !!role && serverMine;
  const loadedRef = useRef(loaded);
  loadedRef.current = loaded;
  const key = storageKey(who.userId, who.salonId);

  const [state, setState] = useState(() => readLocal(key) || blank());
  const [open, setOpen] = useState(false);
  const keyRef = useRef(key);
  const stateRef = useRef(state);
  stateRef.current = state;
  const onboardingRef = useRef(onboarding);
  onboardingRef.current = onboarding;
  // Opening setup re-reads what's already in place, so a chapter finished
  // elsewhere (services in the catalogue, hours in Team) shows as done.
  const refreshFacts = ob.refreshFacts;
  useEffect(() => { if (open && refreshFacts) refreshFacts({ force: true }); }, [open, refreshFacts]);

  /* ---------- this browser's copy: written shortly after a change ----------
   * A chapter can change the plan on every keystroke. The copy here is
   * written once things settle, and straight away when the page is hidden
   * or closed, or the account changes. */
  const localPending = useRef(null); // { key, value }
  const localTimer = useRef(null);
  const flushLocal = useCallback(() => {
    if (localTimer.current) { clearTimeout(localTimer.current); localTimer.current = null; }
    const p = localPending.current;
    localPending.current = null;
    if (p) writeLocal(p.key, p.value);
  }, []);
  const queueLocal = useCallback((value) => {
    localPending.current = { key: keyRef.current, value };
    if (localTimer.current) clearTimeout(localTimer.current);
    localTimer.current = setTimeout(flushLocal, 300);
  }, [flushLocal]);

  /* ---------- the server's copy: sent after changes, retried if it fails ----------
   * Everyone's plan syncs through the same endpoint. The account holder's is
   * the salon's plan; anyone else (an invited stylist, employed staff) gets
   * their own slot, so a stylist never overwrites the owner's. */
  const pending = useRef(null); // the copy the server hasn't got yet
  const serverTimer = useRef(null);
  const sendNow = useCallback(async () => {
    if (serverTimer.current) { clearTimeout(serverTimer.current); serverTimer.current = null; }
    const payload = pending.current;
    if (!payload || !role || !patchOnboarding || !loadedRef.current) return;
    pending.current = null;
    const ok = await patchOnboarding({ checklist: { setup: payload } });
    if (ok === false && !pending.current && keyRef.current === key) {
      // It didn't take. Keep it, and try again shortly.
      pending.current = payload;
      serverTimer.current = setTimeout(() => { serverTimer.current = null; sendNow(); }, 5000);
    }
  }, [role, patchOnboarding, key]);
  const queueServer = useCallback((next) => {
    if (!role || !patchOnboarding) return;
    pending.current = next;
    if (!loadedRef.current) return; // sent once the server's copy is in and merged
    if (serverTimer.current) clearTimeout(serverTimer.current);
    serverTimer.current = setTimeout(() => { serverTimer.current = null; sendNow(); }, 450); // coalesced while typing
  }, [role, patchOnboarding, sendNow]);
  useEffect(() => () => {
    if (serverTimer.current) clearTimeout(serverTimer.current);
    flushLocal();
  }, [flushLocal]);

  // Leaving the page (tab hidden, app backgrounded, closed): write and send
  // anything still waiting now, rather than lose it with the timer.
  useEffect(() => {
    const flush = () => { flushLocal(); if (pending.current && loadedRef.current) sendNow(); };
    const onVis = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', flush);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('pagehide', flush); };
  }, [sendNow, flushLocal]);

  // Switching account or location: load that person's own plan. Anything
  // unsent belongs to the old one, and never goes to the new salon. (A
  // pending browser write still lands, under the old key.)
  useEffect(() => {
    if (keyRef.current === key) return;
    flushLocal();
    keyRef.current = key;
    pending.current = null;
    if (serverTimer.current) { clearTimeout(serverTimer.current); serverTimer.current = null; }
    const fresh = readLocal(key) || blank();
    stateRef.current = fresh; // before any merge that runs in this same pass
    setState(fresh);
    setOpen(false);
  }, [key, flushLocal]);

  // The server's copy arrives after the first render (and again after each
  // write). Merge it in; if this browser has something the server doesn't,
  // send the merged copy back. Nothing is sent when the two already agree.
  const serverSetup = loaded && onboarding.checklist ? onboarding.checklist.setup : null;
  useEffect(() => {
    if (!loaded || keyRef.current !== key) return;
    const cur = stateRef.current;
    const server = serverSetup && serverSetup.updatedAt ? serverSetup : null;
    const merged = server ? mergeSetup(cur, server) : cur;
    if (!same(merged, cur)) { stateRef.current = merged; queueLocal(merged); setState(merged); }
    if (merged && merged.updatedAt && !same(merged, server)) queueServer(merged);
  }, [loaded, serverSetup, key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Another tab on this browser moved on: take its work too.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== keyRef.current || !e.newValue) return;
      let other = null;
      try { other = JSON.parse(e.newValue); } catch (err) { return; }
      const cur = stateRef.current;
      const merged = mergeSetup(cur, other);
      if (same(merged, cur)) return;
      stateRef.current = merged;
      setState(merged);
      if (!same(merged, other)) queueLocal(merged);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [queueLocal]);

  // Write-through: this browser shortly, the server shortly after.
  const persist = useCallback((next) => {
    queueLocal(next);
    queueServer(next);
  }, [queueLocal, queueServer]);

  const update = useCallback((fn) => {
    setState((cur) => {
      const next = { ...fn(cur), updatedAt: now() };
      persist(next);
      return next;
    });
  }, [persist]);

  /* ---------- the plan for this person ---------- */
  // state.track 'location': a location just added, set up with the owner
  // chapters that act on the active site (setupPlan.js).
  // The server also says when this site is a location ADDED to an existing
  // business (onboarding.addedLocation), so the location plan holds on any
  // device, not only the browser that added it (its hand-off note is one-shot).
  const addedLocation = !!(loaded && role === 'owner' && onboarding && onboarding.addedLocation);
  const track = state.track || (addedLocation ? 'location' : undefined);
  const chapters = useMemo(() => (role ? chaptersFor(role, state.answers, track) : []), [role, state.answers, track]);
  // Done in Bailey, OR already in place in the salon however it got there
  // (Beu's team, a spreadsheet upload, the catalogue, Team, Settings) — the
  // server reads those facts from the data (onboarding.facts). The data wins
  // over "later": a step whose data is in is done, even if they skipped it
  // here (JB Studios: clients put in by Beu, plan still said "later").
  const facts = useMemo(() => (loaded && onboarding && onboarding.facts) || {}, [loaded, onboarding]);
  const statusOf = useCallback((id) => statusFrom((state.chapters || {})[id], facts[id]), [state.chapters, facts]);
  const firstOpen = useMemo(() => (chapters.find((c) => !['done', 'skipped'].includes(statusOf(c.id))) || null), [chapters, statusOf]);
  const currentId = state.current;
  const current = useMemo(() => {
    const c = chapters.find((x) => x.id === currentId);
    return c || firstOpen || chapters[chapters.length - 1] || null;
  }, [chapters, currentId, firstOpen]);
  const doneCount = chapters.filter((c) => statusOf(c.id) === 'done').length;
  const settledCount = chapters.filter((c) => ['done', 'skipped'].includes(statusOf(c.id))).length;
  // "Later" is still to do, so it counts towards the time left.
  const minutesLeft = totalMinutes(chapters.filter((c) => statusOf(c.id) !== 'done'));
  const index = current ? chapters.findIndex((c) => c.id === current.id) : 0;

  /* ---------- should the plan be offered at all? ----------
   * Not to an established business (the server says setupEligible: false
   * once the salon, or this person there, has real completed bookings) that
   * hasn't started it, and not to a salon that went live before Bailey's
   * setup existed. Asking for it by name (Settings, the demo page) still
   * opens it; it just isn't pushed on them. */
  const checklist = (loaded && onboarding.checklist) || {};
  const established = loaded && onboarding.setupEligible === false;
  const untouched = ['new', 'parked'].includes(state.status) && settledCount === 0;
  const liveAlready = loaded && role === 'owner' && (onboarding.status === 'completed' || isLiveDone(checklist));
  const offered = !!role && loaded && chapters.length > 0
    && !(established && untouched)
    && !(liveAlready && state.status === 'new');

  /* ---------- actions ---------- */
  const start = useCallback((opts = {}) => {
    update((cur) => {
      // A location just added (opts.track 'location'): its own plan here,
      // from the top, seeded with what the business already told Bailey
      // (opts.seed, e.g. { about }) so the chapters know how it works.
      // The derived track already running here (another device got there
      // first) isn't a new plan: record it, keep the progress.
      if (opts.track && !cur.track && opts.track === track && Object.keys(cur.chapters || {}).length) {
        return { ...cur, track: opts.track, status: cur.status === 'done' ? 'done' : 'active', startedAt: cur.startedAt || now(), current: opts.chapter || cur.current || null };
      }
      if (opts.track && opts.track !== cur.track) {
        return {
          ...cur,
          track: opts.track,
          status: 'active',
          startedAt: cur.startedAt || now(),
          current: opts.chapter || null,
          chapters: {},
          steps: {},
          answers: { ...(opts.seed || {}), ...(cur.answers || {}) },
        };
      }
      return {
        ...cur,
        status: cur.status === 'done' ? 'done' : 'active',
        startedAt: cur.startedAt || now(),
        current: opts.chapter || cur.current || null,
      };
    });
    // For a stylist or staff member this is their own status, not the salon's.
    if (role && patchOnboarding && loadedRef.current && onboarding && ['new', 'dismissed'].includes(onboarding.status)) {
      patchOnboarding({ status: 'in_progress' });
    }
    setOpen(true);
  }, [update, role, patchOnboarding, onboarding, track]);

  // Started before the server's copy had loaded: tell the server once it has.
  useEffect(() => {
    if (!loaded || !onboarding || !patchOnboarding) return;
    if (['active', 'parked'].includes(stateRef.current.status) && ['new', 'dismissed'].includes(onboarding.status)) {
      patchOnboarding({ status: 'in_progress' });
    }
  }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // A stylist or member of staff who has finished: their own status says so
  // on the server, so no device offers it again. (The owner's is set by
  // going live, in Ready.)
  const serverStatus = loaded ? onboarding.status : null;
  useEffect(() => {
    if (!loaded || role === 'owner' || !patchOnboarding) return;
    if (state.status === 'done' && serverStatus !== 'completed') patchOnboarding({ status: 'completed' });
  }, [loaded, role, state.status, serverStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(() => setOpen(false), []);

  // "Not now" at the end of the welcome: nothing is switched off. The plan
  // waits with Bailey until they come back to it.
  const park = useCallback(() => {
    update((cur) => ({ ...cur, status: cur.status === 'done' ? 'done' : 'parked', startedAt: cur.startedAt || now() }));
    setOpen(false);
  }, [update]);

  const goTo = useCallback((id) => update((cur) => ({ ...cur, current: id })), [update]);

  const nextAfter = useCallback((id, marks) => {
    const settled = (c) => ['done', 'skipped'].includes(marks[c.id]);
    const i = chapters.findIndex((c) => c.id === id);
    const after = chapters.slice(i + 1).find((c) => !settled(c));
    const before = chapters.slice(0, i).find((c) => !settled(c));
    return (after || before || null);
  }, [chapters]);

  const complete = useCallback((id, { advance = true } = {}) => {
    update((cur) => {
      const marks = { ...(cur.chapters || {}), [id]: 'done' };
      const next = advance ? nextAfter(id, marks) : null;
      // Every chapter done is finished, for any role: stylists and staff
      // have no go-live step to say so.
      const allDone = chapters.length > 0 && chapters.every((c) => marks[c.id] === 'done');
      return {
        ...cur,
        chapters: marks,
        current: advance ? (next ? next.id : id) : cur.current,
        ...(allDone ? { status: 'done', finishedAt: cur.finishedAt || now() } : {}),
      };
    });
  }, [update, nextAfter, chapters]);

  const skip = useCallback((id) => {
    update((cur) => {
      const marks = { ...(cur.chapters || {}), [id]: (cur.chapters || {})[id] === 'done' ? 'done' : 'skipped' };
      const next = nextAfter(id, marks);
      return { ...cur, chapters: marks, current: next ? next.id : id };
    });
  }, [update, nextAfter]);

  const reopenChapter = useCallback((id) => {
    update((cur) => {
      const marks = { ...(cur.chapters || {}) };
      delete marks[id];
      return { ...cur, chapters: marks, current: id };
    });
  }, [update]);

  const setStep = useCallback((id, step) => update((cur) => ({ ...cur, steps: { ...(cur.steps || {}), [id]: step } })), [update]);

  const answer = useCallback((section, patch) => update((cur) => ({
    ...cur,
    answers: { ...(cur.answers || {}), [section]: { ...((cur.answers || {})[section] || {}), ...patch } },
  })), [update]);

  const confirm = useCallback((k, v = true) => update((cur) => ({ ...cur, confirm: { ...(cur.confirm || {}), [k]: v } })), [update]);

  const markStylist = useCallback((staffId, v) => update((cur) => ({ ...cur, stylistsReady: { ...(cur.stylistsReady || {}), [staffId]: v } })), [update]);

  const finish = useCallback(() => update((cur) => ({ ...cur, status: 'done', finishedAt: now() })), [update]);

  /* ---------- the rehearsal's practice tasks, done in the real app ----------
   * While setup is closed the rehearsal isn't on screen, so this listens for
   * the pages reporting each practice task and moves the resume pill on.
   * Only while armed: the owner tapped a practice task's button in the last
   * few hours (the rehearsal saves { armed, armedAt } with its step). The
   * rehearsal disarms it as soon as it's back on screen, and it goes with
   * the plan on sign-out or a switch of account. */
  const rehStep = (state.steps || {}).rehearsal || null;
  const armedAt = role === 'owner' && loaded && rehStep && rehStep.armed && rehStep.id === 'diary' ? String(rehStep.armedAt || '') : '';
  useEffect(() => {
    if (!armedAt) return undefined;
    const since = new Date(armedAt).getTime();
    if (!Number.isFinite(since) || Date.now() - since > PRACTICE_ARM_MS) return undefined;
    const on = (e) => {
      const k = e && e.detail && e.detail.key;
      if (!PRACTICE_TASKS.some((t) => t.key === k) || Date.now() - since > PRACTICE_ARM_MS) return;
      const ch = (onboardingRef.current && onboardingRef.current.checklist) || {};
      const tasks = ch.tasks || {};
      if (tasks[k] === 'done') return;
      // A checkout counts only on the practice booking itself.
      const practice = ch.practice || (((stateRef.current.answers || {}).rehearsal || {}).practice) || null;
      if (k === 'pay' && practice && practice.bookingId && String(e.detail.bookingId || '') !== String(practice.bookingId)) return;
      const next = { ...tasks, [k]: 'done' };
      // The checklist merge is shallow: always send the whole tasks map.
      if (patchOnboarding) patchOnboarding({ checklist: { tasks: next } });
      hapticSuccess();
      const left = PRACTICE_TASKS.find((t) => !['done', 'skipped'].includes(next[t.key]));
      const doneN = PRACTICE_TASKS.filter((t) => next[t.key] === 'done').length;
      setStep('rehearsal', { ...practiceDiaryStep(doneN, left ? left.pill : 'Practice run done. Back to the rehearsal'), ...(left ? { armed: true, armedAt } : {}) });
    };
    window.addEventListener(FIRST_TASK_EVENT, on);
    return () => window.removeEventListener(FIRST_TASK_EVENT, on);
  }, [armedAt, patchOnboarding, setStep]);

  // Staging tools: start the plan again from nothing, here and on the server.
  const reset = useCallback(() => {
    const at = now();
    const next = { ...blank(), updatedAt: at, resetAt: at };
    pending.current = null;
    localPending.current = null;
    if (localTimer.current) { clearTimeout(localTimer.current); localTimer.current = null; }
    if (serverTimer.current) { clearTimeout(serverTimer.current); serverTimer.current = null; }
    writeLocal(keyRef.current, next);
    stateRef.current = next;
    setState(next);
    setOpen(false);
    if (role && patchOnboarding) patchOnboarding({ checklist: { setup: next } });
  }, [role, patchOnboarding]);

  const value = useMemo(() => ({
    enabled: SETUP_V2_ENABLED && !!role && !setupOff,
    // Nothing is offered until the server's copy of this person's plan is in.
    ready: loaded,
    // Whether to push the plan at all (the pill, the Settings card, opening
    // on arrival). An explicit openSetup() still opens it.
    offered,
    established,
    liveAlready,
    serverStatus,
    welcomeDone: loaded && !!checklist.welcomeDone,
    role,
    state,
    // The plan's track ('location' at an added location), derived when the
    // saved state doesn't carry one. Read this, not state.track.
    track,
    open,
    chapters,
    current,
    index,
    total: chapters.length,
    doneCount,
    settledCount,
    minutesLeft,
    statusOf,
    facts,
    start,
    close,
    park,
    goTo,
    complete,
    skip,
    reopenChapter,
    setStep,
    answer,
    confirm,
    markStylist,
    finish,
    reset,
  }), [setupOff, role, loaded, offered, established, liveAlready, serverStatus, checklist.welcomeDone, state, track, open, chapters, current, index, doneCount, settledCount, minutesLeft, statusOf, facts, start, close, park, goTo, complete, skip, reopenChapter, setStep, answer, confirm, markStylist, finish, reset]);

  return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>;
}

export const useSetup = () => useContext(SetupContext);
